/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { TextArea, TextInput } from '@carbon/react';
import { observer } from 'mobx-react';

import { isValidPEMKey, isValidCertificate } from 'features/git-sync/settings/fields/validations';
import gitSettingsStore from 'features/git-sync/settings/GitSettingsStore';
import { projectStore } from 'stores';
import { hasAccess, actions } from 'utils/user-access';
import * as Styled from 'features/git-sync/settings/fields/GitFields.styled';
import { RepositoryDetailsFields } from 'features/git-sync/settings/fields/RepositoryDetailsFields';
import { CertificatePlaceholder, PEMKeyPlaceholder } from 'features/git-sync/constants';

export const FIELDS_VALIDATION = {
  clientId: { required: true },
  tenantId: { required: true },
  pemKey: {
    required: true,
    customValidation: isValidPEMKey
  },
  certificate: {
    required: true,
    customValidation: isValidCertificate
  }
};

export const FIELDS_THAT_REQUIRE_RESYNC = ['clientId', 'tenantId'];

export const AzureFields = observer(function AzureFields({ onChangeSetting, onInputBlur, errors }) {
  const project = projectStore.project;
  const canModifySettings = hasAccess(project, actions.MODIFY_GIT_SYNC_SETTINGS);
  const canReplacePrivateKey =
    canModifySettings && gitSettingsStore.getCurrentSetting('pemKey') && gitSettingsStore.isSettingSaved('pemKey');
  const canReplaceCertificate =
    canModifySettings &&
    gitSettingsStore.getCurrentSetting('certificate') &&
    gitSettingsStore.isSettingSaved('certificate');

  const handleChange = (event) => {
    const { id, value } = event.target;
    onChangeSetting(id, value);
  };

  const handleBlur = (event) => {
    const { id, value } = event.target;
    onInputBlur(id, value);
  };

  return (
    <>
      <TextInput
        id="clientId"
        labelText="Application (client) ID"
        placeholder="Enter application (client) ID"
        value={gitSettingsStore.getCurrentSetting('clientId')}
        invalid={Boolean(errors['clientId'])}
        invalidText={errors['clientId']}
        onChange={handleChange}
        onBlur={handleBlur}
      />

      <TextInput
        id="tenantId"
        labelText="Directory (tenant) ID"
        placeholder="Enter directory (tenant) ID"
        value={gitSettingsStore.getCurrentSetting('tenantId')}
        invalid={Boolean(errors['tenantId'])}
        invalidText={errors['tenantId']}
        onChange={handleChange}
        onBlur={handleBlur}
      />

      {canReplacePrivateKey ? (
        <Styled.InlineButtons>
          <TextInput
            id="pemKey"
            labelText="Private key"
            placeholder="Enter PEM key"
            type="password"
            value="AcmeKey"
            disabled
          />
          <Styled.InlineDeleteButton size="sm" kind="tertiary" onClick={() => onChangeSetting('pemKey', false)}>
            Replace private key
          </Styled.InlineDeleteButton>
        </Styled.InlineButtons>
      ) : (
        <TextArea
          id="pemKey"
          rows={4}
          labelText="Private key"
          placeholder={PEMKeyPlaceholder}
          invalid={Boolean(errors['pemKey'])}
          invalidText={errors['pemKey']}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      )}

      {canReplaceCertificate ? (
        <Styled.InlineButtons>
          <TextInput
            id="certificate"
            labelText="Certificate"
            placeholder="Enter certificate"
            type="password"
            value="AcmeKey"
            disabled
          />
          <Styled.InlineDeleteButton size="sm" kind="tertiary" onClick={() => onChangeSetting('certificate', false)}>
            Replace certificate
          </Styled.InlineDeleteButton>
        </Styled.InlineButtons>
      ) : (
        <TextArea
          id="certificate"
          rows={4}
          labelText="Certificate"
          placeholder={CertificatePlaceholder}
          invalid={Boolean(errors['certificate'])}
          invalidText={errors['certificate']}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      )}

      <RepositoryDetailsFields
        providerLabel="Azure DevOps"
        repositoryFieldPlaceholder="https://dev.azure.com/camunda-org/camunda-project/_git/camunda-repository"
        onChangeSetting={onChangeSetting}
        onInputBlur={onInputBlur}
        errors={errors}
      />
    </>
  );
});
